<!--
    CamerasContainer.svelte

    This component displays a collection of WebRTC video streams in a responsive layout.
    It supports two display modes controlled by the isOnOneLine prop:

    1. Single-line mode (isOnOneLine = true):
       - Videos are displayed in a single horizontal line
       - Video sizes are automatically adjusted to fit the container width
       - If container is too narrow, videos will maintain minimum width (120px) and overflow horizontally
       - Videos are centered in the container with equal spacing
       - No vertical scrolling

    2. Multi-line mode (isOnOneLine = false):
       - Videos wrap to multiple lines to maximize available space
       - Uses an optimal layout algorithm that:
         a) Calculates maximum possible videos per row at minimum size
         b) Works backwards to find the largest video size that fits without scrolling
         c) Maintains 16:9 aspect ratio for all videos
         d) Ensures videos never go below minimum width (120px)
       - Videos are aligned to the top of the container
       - Vertical scrolling is enabled if needed
       - Maintains consistent gap between videos

    The component automatically adjusts its layout when:
    - Container dimensions change
    - Number of videos changes
    - Display mode changes

    Props:
    - oneLineMaxHeight: Maximum height for videos in single-line mode
    - isOnOneLine: Toggle between single-line and multi-line modes

-->
<script lang="ts">
    import { onMount, setContext } from "svelte";
    import { myCameraPeerStore } from "../../Stores/StreamableCollectionStore";
    import type { VideoBox as VideoBoxModel } from "../../Space/VideoBox";
    import VideoBox from "../Video/VideoBox.svelte";
    import MediaBox from "../Video/MediaBox.svelte";
    import { highlightedEmbedScreen } from "../../Stores/HighlightedEmbedScreenStore";
    import { highlightFullScreen } from "../../Stores/ActionsCamStore";
    import { localUserStore } from "../../Connection/LocalUserStore";
    import { analyticsClient } from "../../Administration/AnalyticsClient";
    import { MAX_DISPLAYED_VIDEOS } from "../../Enum/EnvironmentVariable";
    import {
        orderedStreamableCollectionStore,
        maxVisibleVideosStore,
    } from "../../Stores/OrderedStreamableCollectionStore";
    import { activePictureInPictureStore } from "../../Stores/PeerStore";
    import { oneLineStreamableCollectionStore } from "../../Stores/OneLineStreamableCollectionStore";
    import type { ObservableElement } from "../../Interfaces/ObservableElement";
    import ChevronLeftIcon from "../Icons/ChevronLeftIcon.svelte";
    import ChevronRightIcon from "../Icons/ChevronRightIcon.svelte";
    import ChevronUpIcon from "../Icons/ChevronUpIcon.svelte";
    import ChevronDownIcon from "../Icons/ChevronDownIcon.svelte";
    import {
        computePictureInPictureGridLayout,
        pipGridTemplateColumns,
        pipGridTemplateRows,
        pipTileStyle,
        PIP_GRID_MAX_VIDEOS,
    } from "../Video/PictureInPicture/pictureInPictureGridLayout";
    import ResizeHandle from "./ResizeHandle.svelte";

    setContext("inCameraContainer", true);

    let gap = 16; // Configurable gap between videos in pixels

    // The "maximum" number of videos we want to display.
    // This is not 100% accurate, as if we are in "solution 2", the maximum number of videos
    // will be maximumVideosPerPage + nbVideos % vpr
    const maximumVideosPerPage = MAX_DISPLAYED_VIDEOS;

    interface Props {
        oneLineMaxHeight: number;
        isOnOneLine: boolean;
        oneLineMode: "vertical" | "horizontal";
    }

    let { oneLineMaxHeight, isOnOneLine, oneLineMode = "horizontal" }: Props = $props();

    let containerWidth: number = $state(0);
    let maxContainerHeight: number = $state(0);
    let containerHeight: number = $state(0);
    let camerasContainerHeight: number = $state(0);
    let videoWidth: number = $state(0);
    let videoHeight: number | undefined = $state();
    let camerasContainer: HTMLDivElement | undefined = $state();

    // The minimum width of a media box in pixels
    const minMediaBoxWidth = 160;

    /** Webcam locale dans `streamableCollectionStore` (`myCameraPeerStore`). */
    const LOCAL_CAMERA_VIDEO_BOX_UNIQUE_ID = "-1";

    function excludeLocalCamera(videoBoxes: VideoBoxModel[]): VideoBoxModel[] {
        return videoBoxes.filter((box) => box.uniqueId !== LOCAL_CAMERA_VIDEO_BOX_UNIQUE_ID);
    }

    let isPictureInPictureGridMode = $derived($activePictureInPictureStore && oneLineMode === "vertical");
    let pipDockLocalCamera = $derived(
        isPictureInPictureGridMode &&
            $oneLineStreamableCollectionStore.length > PIP_GRID_MAX_VIDEOS &&
            $oneLineStreamableCollectionStore.some((box) => box.uniqueId === LOCAL_CAMERA_VIDEO_BOX_UNIQUE_ID),
    );
    let pipRemoteParticipantCount = $derived(excludeLocalCamera($oneLineStreamableCollectionStore).length);

    // Single IntersectionObserver shared across all VideoBox components
    let intersectionObserver: IntersectionObserver | undefined = $state();

    onMount(() => {
        // Create the IntersectionObserver once camerasContainer is bound
        if (camerasContainer) {
            intersectionObserver = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        const element = entry.target as ObservableElement;
                        if (element.visibilityCallback) {
                            element.visibilityCallback(entry.isIntersecting);
                        }
                    });
                },
                {
                    root: camerasContainer,
                    threshold: 0,
                },
            );
        }

        // Subscriptions for store changes
        const unsubscriber = orderedStreamableCollectionStore.subscribe((orderedStreamableCollection) => {
            // Sort the collection by priority
            const sortedCollection = [...orderedStreamableCollection].sort((a, b) => b.priority - a.priority);
            // Each time the order of the videos changes, we update the displayOrder of each videoBox
            for (let i = 0; i < sortedCollection.length; i++) {
                sortedCollection[i].displayOrder.set(i);
            }
        });

        return () => {
            unsubscriber();
            if (intersectionObserver) {
                intersectionObserver.disconnect();
            }
        };
    });

    let maxMediaBoxWidth = $derived((oneLineMaxHeight * 16) / 9);

    $effect(() => {
        if (!isOnOneLine) {
            containerHeight = maxContainerHeight * localUserStore.getCameraContainerHeight();
            if (camerasContainer) {
                camerasContainer.style.height = `${containerHeight}px`;
            }
        } else {
            if (camerasContainer) {
                camerasContainer.style.height = "";
            }
        }
    });

    $effect(() => {
        if (isOnOneLine) {
            const oneLineCount = Math.max(1, $oneLineStreamableCollectionStore.length);
            const pipVerticalGrid = $activePictureInPictureStore && oneLineMode === "vertical" && isOnOneLine;
            const pipSortWindowCount = pipDockLocalCamera ? pipRemoteParticipantCount : oneLineCount;

            if (oneLineMode === "horizontal") {
                const countForTileSizing = pipVerticalGrid
                    ? Math.min(PIP_GRID_MAX_VIDEOS, pipSortWindowCount)
                    : oneLineCount;
                const currentVideoWidth = Math.max(
                    Math.min(maxMediaBoxWidth, containerWidth / countForTileSizing),
                    minMediaBoxWidth,
                );
                videoWidth = currentVideoWidth;
                videoHeight = undefined;
                if (pipVerticalGrid) {
                    maxVisibleVideosStore.set(Math.min(PIP_GRID_MAX_VIDEOS, pipSortWindowCount));
                } else {
                    maxVisibleVideosStore.set(Math.ceil(containerWidth / currentVideoWidth));
                }
            } else {
                const currentVideoWidth = containerWidth;
                const currentVideoHeight = currentVideoWidth * (9 / 16);
                videoWidth = currentVideoWidth;
                videoHeight = currentVideoHeight;
                if (pipVerticalGrid) {
                    maxVisibleVideosStore.set(Math.min(PIP_GRID_MAX_VIDEOS, pipSortWindowCount));
                } else {
                    maxVisibleVideosStore.set(Math.ceil(containerHeight / currentVideoHeight));
                }
            }
        } else {
            const layout = calculateOptimalLayout(containerWidth, containerHeight);
            videoWidth = layout.videoWidth;
            videoHeight = layout.videoHeight;
        }
    });

    function calculateOptimalLayout(containerWidth: number, containerHeight: number) {
        if (!containerWidth || !containerHeight) {
            return {
                videoWidth: minMediaBoxWidth,
            };
        }

        // When the user scroll in or out, the canvas is resize and "containerWidth" has a small jitter.
        // When the user is not resizing the container through the resize handle, we don't want to take into account the jitter.
        // Rules: Apply -2 pixels to the gap when the user is not resizing the container.
        // TODO: find a better way to detect this and fix the jitter from the WaScalerManager.
        if (resizeInProgress) {
            gap = 16;
        } else {
            gap = 20;
        }

        // Calculate maximum number of videos that can fit in one row at minimum size
        const maxVideosPerRow = Math.min(
            Math.floor((containerWidth + gap) / (minMediaBoxWidth + gap)),
            $oneLineStreamableCollectionStore.length,
        );

        let lastValidConfig = null;

        // Start with maximum possible videos per row and work backwards
        for (let vpr = maxVideosPerRow; vpr >= 1; vpr--) {
            // Calculate video width based on container width and gap
            const width = (containerWidth - gap * (vpr - 1)) / vpr;

            // Calculate video height maintaining aspect ratio
            const height = (width * 9) / 16;

            // Check if this height would fit in the container
            //if (height <= containerHeight) {
            // Calculate how many complete rows we can fit
            const rowsPerPage = Math.floor((containerHeight + gap) / (height + gap));

            const maxVisibleVideos = rowsPerPage * vpr;

            // The "maximum" number of videos we want to display. This is either the number of videos we have
            // or the maximumVideosPerPage constant.
            // This is not 100% accurate, as if we are in "solution 2", the maximum number of videos
            // will be maximumVideosPerPage + nbVideos % vpr
            const maxNbVideos = Math.min($oneLineStreamableCollectionStore.length, maximumVideosPerPage);
            // If we need scrolling, calculate the maximum height that would fit
            if (maxVisibleVideos < maxNbVideos) {
                // Calculate total number of rows needed
                const totalRows = Math.ceil(maxNbVideos / vpr);

                // Special case: we are on one row only, and we need to adapt the width / height of the videos to the container height
                if (totalRows === 1) {
                    const adjustedWidth = (containerHeight * 16) / 9;
                    // We put the maximum number of visible videos in a store. This store will be used to show active participants first.
                    maxVisibleVideosStore.set(vpr);

                    return {
                        videoWidth: Math.max(adjustedWidth, minMediaBoxWidth),
                    };
                }

                // There are 2 possible optimal solutions here. Either we can reduce the height of the videos
                // to fit in the container OR we can take the previous solution with one more video per row
                // Let's check which one is better (i.e. which one has the largest video size)

                // Solution 1: let's reduce video size:

                // Calculate maximum height per video that would fit
                const maxHeightPerVideo = (containerHeight - gap * (totalRows - 1)) / totalRows;

                // Calculate corresponding width based on aspect ratio
                const adjustedWidthWithReducedHeight = (maxHeightPerVideo * 16) / 9;

                // Solution 2: let's increase the number of videos per row (only possible if we have enough videos)
                const adjustedWidthWithOneMoreVpr =
                    maxNbVideos >= vpr + 1 ? (containerWidth - gap * vpr) / (vpr + 1) : 0;

                // Check which solution is better
                let adjustedWidth: number;
                let adjustedHeight: number | undefined;
                if (adjustedWidthWithReducedHeight > adjustedWidthWithOneMoreVpr) {
                    const adjustedVpr = Math.floor((containerWidth + gap) / (adjustedWidthWithReducedHeight + gap));
                    if (adjustedVpr !== vpr) {
                        console.warn("problem");
                    }
                    // if solution 1 is better
                    adjustedWidth = adjustedWidthWithReducedHeight;
                    // We put the maximum number of visible videos in a store. This store will be used to show active participants first.
                    maxVisibleVideosStore.set(vpr * (rowsPerPage + 1));
                } else {
                    // if solution 2 is better, the videos will not occupy all vertical space.
                    // We can fix this by breaking the aspect ratio.
                    adjustedWidth = adjustedWidthWithOneMoreVpr;
                    const adjustedTotalRows = Math.ceil(maxNbVideos / (vpr + 1));
                    adjustedHeight = (containerHeight - gap * (adjustedTotalRows - 1)) / adjustedTotalRows;
                    if (adjustedHeight < adjustedWidth * (9 / 16)) {
                        adjustedHeight = adjustedWidth * (9 / 16);
                    }
                    // We put the maximum number of visible videos in a store. This store will be used to show active participants first.
                    maxVisibleVideosStore.set((vpr + 1) * adjustedTotalRows);
                }

                if (adjustedWidth < minMediaBoxWidth) {
                    return {
                        videoWidth: minMediaBoxWidth,
                    };
                }
                //const adjustedWidth = Math.max(adjustedWidthWithReducedHeight, adjustedWidthWithOneMoreVpr);
                return {
                    videoWidth: adjustedWidth,
                    videoHeight: adjustedHeight,
                };
            }

            // Keep this as our last valid config that doesn't need scrolling
            lastValidConfig = {
                videoWidth: width,
            };
        }

        // If we get here, we never needed scrolling, use the last valid config
        return (
            lastValidConfig || {
                videoWidth: minMediaBoxWidth,
            }
        );
    }

    let grabPointerEvents = $state(false);
    const isWebkit = "WebkitAppearance" in document.documentElement.style;

    $effect(() => {
        // In Webkit, the scroll event on the cameras-container is not triggered when the user scrolls unless the
        // pointer-events is set to auto. But we want to avoid that unless there is a scroll bar to keep the
        // pointer events to go through to the map.

        // Let's trigger this logic when the number of videos changes or when the container width changes
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        $oneLineStreamableCollectionStore;

        if (isWebkit && isOnOneLine && oneLineMode === "horizontal") {
            setTimeout(() => {
                if (camerasContainer) {
                    if (camerasContainer.scrollWidth > containerWidth) {
                        grabPointerEvents = true;
                    } else {
                        grabPointerEvents = false;
                    }
                }
            }, 500);
        } else {
            grabPointerEvents = false;
        }
    });

    let resizeInProgress = $state(false);
    function onResizeHandler(height: number) {
        resizeInProgress = true;
        containerHeight = height;
        const coefCameraContainerHeight = containerHeight / maxContainerHeight;
        localUserStore.setCameraContainerHeight(coefCameraContainerHeight > 0.9 ? 0.9 : coefCameraContainerHeight);
        if (camerasContainer) {
            const oldHeight = camerasContainer.scrollHeight;
            // Move the scroll position to keep the same percentage of position
            const oldScrollPercent = camerasContainer.scrollTop / oldHeight;

            camerasContainer.style.height = `${containerHeight}px`;
            camerasContainer.scrollTop = camerasContainer.scrollHeight * oldScrollPercent;
        }
    }

    // Scroll indicators: show when user can scroll to see more cameras
    let canScrollLeft = $state(false);
    let canScrollRight = $state(false);
    let canScrollTop = $state(false);
    let canScrollBottom = $state(false);
    let pipVideoBoxes = $derived(
        isPictureInPictureGridMode
            ? pipDockLocalCamera
                ? excludeLocalCamera($orderedStreamableCollectionStore).slice(0, PIP_GRID_MAX_VIDEOS)
                : $orderedStreamableCollectionStore.slice(0, PIP_GRID_MAX_VIDEOS)
            : $oneLineStreamableCollectionStore,
    );
    let pipLayout = $derived(
        computePictureInPictureGridLayout(
            pipVideoBoxes.length,
            Math.max(1, containerWidth || 0),
            Math.max(1, camerasContainerHeight || 0),
        ),
    );

    function updateScrollIndicators() {
        if (!camerasContainer) return;
        if (isPictureInPictureGridMode) {
            canScrollLeft = false;
            canScrollRight = false;
            canScrollTop = false;
            canScrollBottom = false;
            return;
        }
        const { scrollLeft, scrollTop, scrollWidth, scrollHeight, clientWidth, clientHeight } = camerasContainer;
        const threshold = 4; // pixels tolerance

        if (isOnOneLine && oneLineMode === "horizontal") {
            canScrollTop = false;
            canScrollBottom = false;
            canScrollLeft = scrollWidth > clientWidth && scrollLeft > threshold;
            canScrollRight = scrollWidth > clientWidth && scrollLeft < scrollWidth - clientWidth - threshold;
        } else {
            canScrollLeft = false;
            canScrollRight = false;
            canScrollTop = scrollHeight > clientHeight && scrollTop > threshold;
            canScrollBottom = scrollHeight > clientHeight && scrollTop < scrollHeight - clientHeight - threshold;
        }
    }

    const scrollStepRatio = 0.85; // scroll ~85% of visible area per click

    function scrollCamerasLeft() {
        if (!camerasContainer) return;
        const step = camerasContainer.clientWidth * scrollStepRatio;
        camerasContainer.scrollBy({ left: -step, behavior: "smooth" });
    }

    function scrollCamerasRight() {
        if (!camerasContainer) return;
        const step = camerasContainer.clientWidth * scrollStepRatio;
        camerasContainer.scrollBy({ left: step, behavior: "smooth" });
    }

    function scrollCamerasUp() {
        if (!camerasContainer) return;
        const step = camerasContainer.clientHeight * scrollStepRatio;
        camerasContainer.scrollBy({ top: -step, behavior: "smooth" });
    }

    function scrollCamerasDown() {
        if (!camerasContainer) return;
        const step = camerasContainer.clientHeight * scrollStepRatio;
        camerasContainer.scrollBy({ top: step, behavior: "smooth" });
    }

    // Re-run scroll indicator check when layout or content changes
    $effect(() => {
        if (camerasContainer) {
            const _exhaustiveCheck = [
                $oneLineStreamableCollectionStore,
                containerWidth,
                containerHeight,
                isOnOneLine,
                oneLineMode,
            ];
            updateScrollIndicators();
        }
    });
</script>

<div
    class="group/cameras-container w-full"
    bind:clientHeight={maxContainerHeight}
    class:h-full={!isOnOneLine || (isOnOneLine && oneLineMode === "vertical")}
>
    <div
        bind:clientWidth={containerWidth}
        bind:clientHeight={camerasContainerHeight}
        bind:this={camerasContainer}
        class="no-scroll-bar mx-1"
        class:justify-center-safe={isOnOneLine && oneLineMode === "horizontal"}
        class:justify-center={!isOnOneLine || oneLineMode !== "horizontal"}
        class:pointer-events-none={!grabPointerEvents}
        class:pointer-events-auto={grabPointerEvents}
        class:hidden={$highlightFullScreen && $highlightedEmbedScreen && oneLineMode !== "vertical"}
        class:flex={!isPictureInPictureGridMode}
        class:grid={isPictureInPictureGridMode}
        style={isPictureInPictureGridMode
            ? `grid-template-columns: ${pipGridTemplateColumns(pipLayout.columnTracks)}; grid-template-rows: ${pipGridTemplateRows(pipLayout.rowTracks)};`
            : ""}
        class:gap-2={isPictureInPictureGridMode}
        class:p-2={isPictureInPictureGridMode}
        class:gap-4={!isPictureInPictureGridMode}
        class:max-h-full={isOnOneLine && oneLineMode === "horizontal"}
        class:max-w-full={!isOnOneLine || (isOnOneLine && oneLineMode === "horizontal")}
        class:flex-col={isOnOneLine && oneLineMode === "vertical" && !isPictureInPictureGridMode}
        class:flex-wrap={!isOnOneLine}
        class:content-start={!isOnOneLine}
        class:whitespace-nowrap={isOnOneLine}
        class:relative={true}
        class:overflow-x-auto={isOnOneLine && oneLineMode === "horizontal" && !isPictureInPictureGridMode}
        class:overflow-x-hidden={!isOnOneLine}
        class:overflow-y-auto={!isOnOneLine ||
            (isOnOneLine && oneLineMode === "vertical" && !isPictureInPictureGridMode)}
        class:overflow-hidden={isPictureInPictureGridMode}
        class:overflow-y-hidden={isOnOneLine && oneLineMode === "horizontal"}
        class:pb-3={isOnOneLine && !$highlightedEmbedScreen}
        class:m-0={isOnOneLine}
        class:my-0={isOnOneLine}
        class:w-full={!isOnOneLine && oneLineMode !== "horizontal"}
        class:items-start={!isOnOneLine}
        class:not-highlighted={!isOnOneLine}
        class:mt-0={!isOnOneLine}
        class:h-full={isOnOneLine && oneLineMode === "vertical"}
        class:m-2={$activePictureInPictureStore}
        id="cameras-container"
        data-testid="cameras-container"
        onscroll={updateScrollIndicators}
    >
        {#each pipVideoBoxes as videoBox, i (videoBox.uniqueId)}
            <div
                style={isPictureInPictureGridMode ? pipTileStyle(pipLayout.tiles[i]) : ""}
                class:min-h-0={isPictureInPictureGridMode}
                class:min-w-0={isPictureInPictureGridMode}
            >
                <VideoBox
                    {videoBox}
                    {isOnOneLine}
                    {oneLineMode}
                    {videoWidth}
                    {videoHeight}
                    intersectionObserver={isPictureInPictureGridMode ? undefined : intersectionObserver}
                    forceDisplay={isPictureInPictureGridMode ||
                        (videoBox.uniqueId === LOCAL_CAMERA_VIDEO_BOX_UNIQUE_ID &&
                            isOnOneLine &&
                            oneLineMode === "vertical" &&
                            !pipDockLocalCamera)}
                    fitContainer={isPictureInPictureGridMode}
                />
            </div>
        {/each}
    </div>
    {#if pipDockLocalCamera}
        <div
            class="pointer-events-auto fixed bottom-20 right-2 z-30 aspect-video w-40 max-w-[14rem] min-w-[8.75rem] overflow-hidden rounded-lg shadow-xl"
            data-testid="pip-local-camera-overlay"
        >
            <MediaBox videoBox={$myCameraPeerStore} />
        </div>
    {/if}
    {#if !isOnOneLine}
        <ResizeHandle
            minHeight={maxContainerHeight * 0.1}
            maxHeight={maxContainerHeight * 0.9}
            onResize={onResizeHandler}
            onResizeEnd={() => {
                resizeInProgress = false;
                analyticsClient.resizeCameraLayout();

                // We need to recalculate the layout to take into account the new container width
                const layout = calculateOptimalLayout(containerWidth, containerHeight);
                videoWidth = layout.videoWidth;
                videoHeight = layout.videoHeight;
            }}
            dataTestid="resize-handle"
        />
    {/if}
    <!-- Scroll buttons: show when more cameras are available, click to scroll -->
    {#if isOnOneLine && oneLineMode === "horizontal"}
        {#if canScrollLeft}
            <button
                type="button"
                class="scroll-indicator scroll-indicator-left scroll-indicator-button opacity-10 group-hover/cameras-container:opacity-100"
                aria-label="Scroll left to see more cameras"
                onclick={scrollCamerasLeft}
            >
                <span class="scroll-indicator-gradient scroll-indicator-gradient-left"></span>
                <span class="scroll-indicator-chevron">
                    <ChevronLeftIcon height="h-8" width="w-8" strokeWidth="2" />
                </span>
            </button>
        {/if}
        {#if canScrollRight}
            <button
                type="button"
                class="scroll-indicator scroll-indicator-right scroll-indicator-button opacity-10 group-hover/cameras-container:opacity-100"
                aria-label="Scroll right to see more cameras"
                onclick={scrollCamerasRight}
            >
                <span class="scroll-indicator-gradient scroll-indicator-gradient-right"></span>
                <span class="scroll-indicator-chevron">
                    <ChevronRightIcon height="h-8" width="w-8" strokeWidth="2" />
                </span>
            </button>
        {/if}
    {:else}
        {#if canScrollTop}
            <button
                type="button"
                class="absolute scroll-indicator scroll-indicator-top scroll-indicator-button opacity-10 group-hover/cameras-container:opacity-100"
                aria-label="Scroll up to see more cameras"
                onclick={scrollCamerasUp}
            >
                <span class="scroll-indicator-gradient scroll-indicator-gradient-top"></span>
                <span class="scroll-indicator-chevron">
                    <ChevronUpIcon height="h-8" width="w-8" strokeWidth="2" />
                </span>
            </button>
        {/if}
        {#if canScrollBottom}
            <button
                type="button"
                class="absolute scroll-indicator scroll-indicator-bottom scroll-indicator-button h-fit w-fit opacity-40 group-hover/cameras-container:opacity-100"
                aria-label="Scroll down to see more cameras"
                onclick={scrollCamerasDown}
            >
                <span class="scroll-indicator-gradient scroll-indicator-gradient-bottom h-full"></span>
                <span class="scroll-indicator-chevron">
                    <ChevronDownIcon height="h-8" width="w-8" strokeWidth="2" />
                </span>
            </button>
        {/if}
    {/if}
</div>

<!-- && !$megaphoneEnabledStore TODO HUGO -->
<style>
    .hidden {
        display: none !important;
    }

    .scroll-indicator {
        z-index: 50;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .scroll-indicator-button {
        pointer-events: auto;
        cursor: pointer;
        border: none;
        margin: 0;
        padding: 0;
        background: transparent;
        font: inherit;
        color: inherit;
        -webkit-tap-highlight-color: transparent;
        width: 100%;
    }

    .scroll-indicator-button:hover .scroll-indicator-chevron,
    .scroll-indicator-button:focus-visible .scroll-indicator-chevron {
        transform: scale(1.1);
        background: rgba(0, 0, 0, 0.75);
    }

    .scroll-indicator-button:focus-visible {
        outline: 2px solid white;
        outline-offset: 2px;
    }

    .scroll-indicator-left {
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 72px;
    }

    .scroll-indicator-right {
        position: absolute;
        right: 0;
        top: 0;
        bottom: 0;
        width: 72px;
    }

    .scroll-indicator-top {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 56px;
    }

    .scroll-indicator-bottom {
        position: relative;
        bottom: 0;
        left: 0;
        right: 0;
        height: 56px;
    }

    .scroll-indicator-gradient {
        position: absolute;
        inset: 0;
        pointer-events: none;
    }

    .scroll-indicator-gradient-left {
        background: linear-gradient(to right, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.2) 70%, transparent 100%);
    }

    .scroll-indicator-gradient-right {
        background: linear-gradient(to left, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.2) 70%, transparent 100%);
    }

    .scroll-indicator-gradient-top {
        background: linear-gradient(to bottom, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.2) 20%, transparent 100%);
    }

    .scroll-indicator-gradient-bottom {
        background: linear-gradient(to top, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.2) 20%, transparent 100%);
    }

    .scroll-indicator-chevron {
        position: relative;
        z-index: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 44px;
        height: 44px;
        min-width: 44px;
        min-height: 44px;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.55);
        color: white;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
        transition:
            transform 0.2s ease,
            background 0.2s ease;
    }

    .scroll-indicator-chevron :global(svg) {
        display: block;
    }

    @container (min-width: 1024) and (max-width: 1279px) {
        .not-highlighted {
            gap: 1rem;
        }
    }

    @container (min-width: 640px) and (max-width: 1024px) {
        .not-highlighted {
            gap: 0.75rem;
        }
    }

    @container (max-width: 640px) {
        .not-highlighted {
            gap: 0.5rem;
        }
    }
    .no-scroll-bar {
        -ms-overflow-style: none;
        scrollbar-width: none;
    }
</style>
